import { Fragment } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { SBStatusEvent } from '@avisoma/shared';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedTd } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTdClass,
  extractionTheadClass,
} from './tableClasses';
import {
  findLiveOpenSupersededRevisions,
  type SupersededSBRevisionRow,
} from './sbOpenRevisionWarning';

type SBRow = SBStatusEvent & {
  pageId?: string;
  sbPageIds?: string[];
  supersededBy?: { sbNumber?: string; revision?: string };
};

/** Columns always rendered; `Page`, `Requirement` and `Superseded By` are added on demand. */
const FIXED_COLUMN_COUNT = 13;

export function SBTable<T extends SBRow>({
  rows,
  pageIdToPosition,
  onInspect,
  showSupersededBy = false,
  supersededRevisions,
  rowFindings,
}: {
  rows: T[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: T, index: number) => void;
  /** DR-2392: render a "Superseded By" column — used only by the revision-history table. */
  showSupersededBy?: boolean;
  /**
   * DR-3307: the project's folded revision history. Supplied only by the live SB
   * table, so each entry can warn when the fold hid an older revision that still
   * prints open with due data.
   */
  supersededRevisions?: SupersededSBRevisionRow[];
  rowFindings?: RowFindings;
}) {
  // DR-5720: the column exists only when a row carries a marker, so a pack
  // with no operator marker keeps the old header.
  const showRequirement = rows.some((row) => Boolean(row.requirement?.trim()));
  const columnCount =
    FIXED_COLUMN_COUNT +
    (rowFindings ? 1 : 0) +
    (pageIdToPosition ? 1 : 0) +
    (showSupersededBy ? 1 : 0) +
    (showRequirement ? 1 : 0);
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Page</Th>}
          <Th>SB Number</Th>
          {showRequirement && <Th>Requirement</Th>}
          <Th>Revision</Th>
          {showSupersededBy && <Th>Superseded By</Th>}
          <Th>Category</Th>
          <Th>Subject</Th>
          <Th>AD Ref</Th>
          <Th>ATA</Th>
          <Th>Engine</Th>
          <Th>Part / S/N</Th>
          <Th>Status</Th>
          <Th>Compliance</Th>
          <Th>Next due</Th>
          <Th>Repeat</Th>
          <Th>Remarks</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((r, i) => (
          <Fragment key={`${r.pageId}-${i}`}>
            <tr onClick={() => onInspect(r, i)} className={extractionRowClass}>
              {rowFindings && <FindingMarkerCell findings={rowFindings.get(i)} />}
              {pageIdToPosition && (
                <Td>
                  <SourcePagesCell row={r} pageIdToPosition={pageIdToPosition} />
                </Td>
              )}
              <Td className="font-mono">{r.sbNumber}</Td>
              {showRequirement && <Td className="font-mono">{r.requirement?.trim() || null}</Td>}
              <Td>{r.revision ?? '—'}</Td>
              {showSupersededBy && <Td className="font-mono">{formatSupersededBy(r)}</Td>}
              <Td>{formatCategory(r)}</Td>
              <TruncatedTd value={r.subject} maxWidth="max-w-[240px]" />
              <Td className="font-mono">{r.adReference ?? '—'}</Td>
              <Td className="font-mono">{r.ataReference ?? '—'}</Td>
              <Td>{r.applicableEngine ?? '—'}</Td>
              <Td className="font-mono">{joinUnits([r.partNumber, r.serialNumber])}</Td>
              <Td>
                <StatusBadge
                  status={r.status}
                  remarksStatus={r.remarksStatus}
                  remarksStatusReason={r.remarksStatusReason}
                />
              </Td>
              <Td>
                {joinUnits([
                  r.complianceDate,
                  r.complianceTsn != null ? `TSN ${r.complianceTsn}` : undefined,
                  r.complianceCsn != null ? `CSN ${r.complianceCsn}` : undefined,
                  r.complianceShopVisit,
                ])}
              </Td>
              <Td>{formatNextDue(r)}</Td>
              <Td>
                {joinUnits([
                  r.repeatIntervalHours != null ? `${r.repeatIntervalHours} hrs` : undefined,
                  r.repeatIntervalCycles != null ? `${r.repeatIntervalCycles} cyc` : undefined,
                  r.repeatIntervalDays != null ? `${r.repeatIntervalDays} days` : undefined,
                  r.repeatIntervalShopVisits != null ? `${r.repeatIntervalShopVisits} SV` : undefined,
                ])}
              </Td>
              <TruncatedTd value={r.remarks} />
            </tr>
            <OpenRevisionWarningRow
              row={r}
              supersededRevisions={supersededRevisions}
              columnCount={columnCount}
            />
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

/**
 * DR-3307: a full-width advisory row under an SB entry whose revision fold hid an
 * older revision that still prints open with live due data. Advisory only — the
 * entry keeps its extracted N/A status; this just says the fold is worth checking.
 * Renders nothing (no row at all) for every other entry.
 */
function OpenRevisionWarningRow({
  row,
  supersededRevisions,
  columnCount,
}: {
  row: SBRow;
  supersededRevisions: SupersededSBRevisionRow[] | undefined;
  columnCount: number;
}) {
  const hidden = findLiveOpenSupersededRevisions(row, supersededRevisions);
  if (hidden.length === 0) return null;

  const labels = hidden.map((r) => (r.revision ? `Rev. ${r.revision}` : 'an unrevised entry')).join(', ');
  const due = hidden.map(formatNextDue).filter((value) => value !== '—').join(' | ');
  const subject = hidden.length > 1 ? `Older revisions ${labels} print` : `Older revision ${labels} prints`;

  return (
    <tr className="bg-amber-50 dark:bg-amber-950/40">
      <td colSpan={columnCount} className={`${extractionTdClass} whitespace-normal`}>
        <span className="flex items-start gap-1.5 text-amber-700 dark:text-amber-300">
          <ExclamationTriangleIcon className="size-3.5 shrink-0 translate-y-px" />
          <span>
            {subject} Open with due data{due ? ` (${due})` : ''} — please confirm.
          </span>
        </span>
      </td>
    </tr>
  );
}

/** The row's forward-looking due thresholds, joined for display; em dash when it has none. */
function formatNextDue(r: SBRow): string {
  return joinUnits([
    r.nextDueDate,
    r.nextDueHours != null ? `${r.nextDueHours} hrs` : undefined,
    r.nextDueCycles != null ? `${r.nextDueCycles} cyc` : undefined,
    r.nextDueShopVisit ?? (r.nextDueAtShopVisit ? 'shop visit' : undefined),
    r.remainingHours != null ? `${r.remainingHours} hrs remaining` : undefined,
    r.remainingCycles != null ? `${r.remainingCycles} cyc remaining` : undefined,
  ]);
}

/** Show the verbatim compliance-category cell value as printed. */
function formatCategory(r: SBRow): string {
  return r.categoryRaw ?? '—';
}

/** DR-2392: the superseding row as "<sbNumber> <revision>" (revision omitted when absent), em dash when unlinked. */
function formatSupersededBy(r: SBRow): string {
  const link = r.supersededBy;
  if (!link?.sbNumber) return '—';
  return link.revision ? `${link.sbNumber} ${link.revision}` : link.sbNumber;
}

function joinUnits(parts: Array<string | undefined>): string {
  return parts.filter(Boolean).join(' · ') || '—';
}

function SourcePagesCell({
  row,
  pageIdToPosition,
}: {
  row: SBRow;
  pageIdToPosition: Map<string, number>;
}) {
  const pageIds = row.sbPageIds ?? (row.pageId ? [row.pageId] : []);
  if (pageIds.length === 0) return <>—</>;
  return (
    <span className="flex flex-col gap-0.5">
      {pageIds.map((pageId) => (
        <SourcePageCell key={pageId} pageId={pageId} pageIdToPosition={pageIdToPosition} />
      ))}
    </span>
  );
}

function StatusBadge({
  status,
  remarksStatus,
  remarksStatusReason,
}: {
  status: string | undefined;
  remarksStatus: SBStatusEvent['remarksStatus'];
  remarksStatusReason: SBStatusEvent['remarksStatusReason'];
}) {
  if (!status) return <>—</>;
  const tone =
    status === 'open'
      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
      : status === 'closed'
        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
        : 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200';
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap ${tone}`}>{status}</span>
      <RemarksStatusWarning status={status} remarksStatus={remarksStatus} remarksStatusReason={remarksStatusReason} />
    </span>
  );
}

/**
 * DR-2597: the row's remarks declare a status that contradicts the status column. Advisory
 * only — the badge keeps showing the extracted status; this just marks the disagreement.
 * Re-checks the inequality rather than trusting the flag's presence: rows reach this table
 * through project-level merges, so a flag that ended up agreeing with the rendered status is
 * not a contradiction and must not be badged. The supporting remark is quoted in the tooltip
 * when the row carries it, so the disagreement can be judged without opening the source page.
 */
function RemarksStatusWarning({
  status,
  remarksStatus,
  remarksStatusReason,
}: {
  status: string;
  remarksStatus: SBStatusEvent['remarksStatus'];
  remarksStatusReason: SBStatusEvent['remarksStatusReason'];
}) {
  if (!remarksStatus || remarksStatus === status) return null;
  const label = remarksStatusReason
    ? `Remarks suggest: ${remarksStatus} — "${remarksStatusReason}"`
    : `Remarks suggest: ${remarksStatus}`;
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className="inline-flex text-amber-600 dark:text-amber-400"
    >
      <ExclamationTriangleIcon className="size-3.5 shrink-0" />
    </span>
  );
}
